"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import type { Editor } from "@tiptap/core";
import { useEditor, EditorContent } from "@tiptap/react";
import BubbleMenu from "@tiptap/extension-bubble-menu";
import StarterKit from "@tiptap/starter-kit";
import { type SetImageOptions } from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { Images as ImagesIcon } from "lucide-react";
import { NodeSelection, TextSelection } from "prosemirror-state";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ResizableImageNode } from "@/components/editor/ResizableImageNode";
import { LayoutBlock } from "@/components/editor/LayoutBlockNode";
import { ImageRow } from "@/components/editor/ImageRowNode";
import { cn } from "@/lib/utils";

/** 找到光标顶层祖先在文档中的前一个兄弟 block。 */
function findPrevTopLevelBlock(state: {
  selection: { $from: any };
  doc: any;
}): { node: any; pos: number } | null {
  try {
    const $from = state.selection.$from;
    const depth1Start: number =
      $from.depth >= 1 ? ($from.before(1) as number) : ($from.pos as number);

    let prevNode: any = null;
    let prevPos = -1;
    state.doc.forEach((node: any, offset: number) => {
      if (offset < depth1Start) {
        prevNode = node;
        prevPos = offset;
      }
    });

    if (prevNode !== null && prevPos >= 0) {
      return { node: prevNode, pos: prevPos };
    }
  } catch {}
  return null;
}

/** 向前最多扫 maxLookback 个顶层 block，找到最近的 imageRow。
 *  解决 insertContent 后出现多余空段落导致 findPrevTopLevelBlock 找不到 imageRow 的问题。
 */
function findPrevImageRowBlock(
  state: { selection: { $from: any }; doc: any },
  maxLookback = 3,
): { node: any; pos: number } | null {
  try {
    const $from = state.selection.$from;
    const depth1Start: number =
      $from.depth >= 1 ? ($from.before(1) as number) : ($from.pos as number);

    const candidates: Array<{ node: any; pos: number }> = [];
    state.doc.forEach((node: any, offset: number) => {
      if (offset < depth1Start) {
        candidates.push({ node, pos: offset });
      }
    });

    for (
      let i = candidates.length - 1;
      i >= Math.max(0, candidates.length - maxLookback);
      i--
    ) {
      if (candidates[i].node.type.name === "imageRow") {
        return candidates[i];
      }
    }
  } catch {}
  return null;
}

type CardPreset = "imageLeft" | "imageRight" | "center";

type ImageInsertResult = {
  src: string;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    image: {
      setImage: (options: SetImageOptions) => ReturnType;
    };
  }
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onUploadImage?: (file: File) => Promise<ImageInsertResult>;
  toolbarSticky?: boolean;
  readOnly?: boolean;
  disableImageActions?: boolean;
  disableLayoutActions?: boolean;
}

export default function RichTextEditor({
  value,
  onChange,
  onUploadImage,
  toolbarSticky,
  readOnly = false,
  disableImageActions = false,
  disableLayoutActions = false,
}: RichTextEditorProps) {
  const t = useTranslations("courses.admin");
  const insertImageLabel = t("buttons.insertImage", {
    defaultValue: "Insert image",
  });
  const insertImageUrlLabel = t("buttons.insertImageUrl", {
    defaultValue: "Insert image from URL",
  });
  const imageUrlPrompt = t("prompts.imageUrl", {
    defaultValue: "Enter image URL",
  });
  const insertLabel = t("buttons.confirm", { defaultValue: "Insert" });
  const cancelLabel = t("buttons.cancel", { defaultValue: "Cancel" });
  const layoutTwoColumnsLabel = t("editor.layoutTwoColumns", {
    defaultValue: "Two-column layout",
  });
  const layoutFullWidthLabel = t("editor.layoutFullWidth", {
    defaultValue: "Full-width block",
  });
  const layoutImageTextCardLabel = t("editor.layoutImageTextCard", {
    defaultValue: "Image & text card",
  });
  const editorRef = useRef<Editor | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingCardPreset, setPendingCardPreset] = useState<CardPreset | null>(
    null,
  );
  const [pendingImageRow, setPendingImageRow] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [imageUrlDraft, setImageUrlDraft] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const lastEmittedHtmlRef = useRef<string>(value || "");

  const readFileAsDataUrl = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          resolve(result);
          return;
        }
        reject(new Error("Failed to read image file"));
      };
      reader.onerror = () =>
        reject(reader.error ?? new Error("Failed to read image file"));
      reader.readAsDataURL(file);
    });
  }, []);

  const resolveImageSrc = useCallback(
    async (file: File) => {
      if (onUploadImage) {
        setImageUploading(true);
        try {
          const uploaded = await onUploadImage(file);
          return uploaded.src;
        } finally {
          setImageUploading(false);
        }
      }
      return readFileAsDataUrl(file);
    },
    [onUploadImage, readFileAsDataUrl],
  );

  const insertImage = useCallback(
    async (file: File) => {
      if (disableImageActions || readOnly) return;
      if (!file || !file.type.startsWith("image/")) return;
      const editor = editorRef.current;
      if (!editor || disableImageActions || readOnly) return;

      try {
        const src = await resolveImageSrc(file);
        const { state } = editor;
        const { selection } = state;

        // 追加一张图片到已有 imageRow 行尾
        const appendToRow = (rowPos: number, rowNode: any) => {
          const rowEndPos = rowPos + rowNode.nodeSize - 1;
          const newImg = state.schema.nodes.image.create({ src });
          const tr = state.tr.insert(rowEndPos, newImg);
          tr.setSelection(NodeSelection.create(tr.doc, rowEndPos));
          editor.view.dispatch(tr.scrollIntoView());
          editor.commands.focus();
        };

        // 1. 光标在 imageRow 内：找到祖先行追加
        if (editor.isActive("imageRow")) {
          const $from = selection.$from;
          for (let d = $from.depth; d >= 0; d--) {
            if ($from.node(d).type.name === "imageRow") {
              appendToRow($from.before(d), $from.node(d));
              return;
            }
          }
        }

        // 辅助：把现有独立图片 + 新图片包裹成 imageRow
        const wrapIntoImageRow = (
          existingNode: any,
          deleteFrom: number,
          deleteTo: number,
          insertAt: number,
        ) => {
          const existingImage = state.schema.nodes.image.create(
            existingNode.attrs,
            null,
            existingNode.marks,
          );
          const newImage = state.schema.nodes.image.create({ src });
          const row = state.schema.nodes.imageRow.create(null, [
            existingImage,
            newImage,
          ]);
          const tr = state.tr
            .delete(deleteFrom, deleteTo)
            .insert(insertAt, row);
          tr.setSelection(
            NodeSelection.create(tr.doc, insertAt + row.nodeSize - 2),
          );
          editor.view.dispatch(tr.scrollIntoView());
          editor.commands.focus();
        };

        // 2. 前驱 block（向前扫3格）是 imageRow → 追加
        const prevImageRow = findPrevImageRowBlock(state);
        if (prevImageRow) {
          appendToRow(prevImageRow.pos, prevImageRow.node);
          return;
        }
        // 3. 文档顶层前驱 block 是独立图片 → 包裹
        const prev = findPrevTopLevelBlock(state);
        if (prev?.node?.type?.name === "image") {
          wrapIntoImageRow(
            prev.node,
            prev.pos,
            prev.pos + prev.node.nodeSize,
            prev.pos,
          );
          return;
        }

        // 4. 当前选中了一张独立图片
        if (selection instanceof NodeSelection) {
          const selectedNode = state.doc.nodeAt(selection.from);
          if (selectedNode?.type.name === "image") {
            wrapIntoImageRow(
              selectedNode,
              selection.from,
              selection.to,
              selection.from,
            );
            return;
          }
        }

        // 5. 光标紧邻前一张独立图片（直接 nodeBefore）
        const $from = selection.$from;
        const beforeNode = ($from as any).nodeBefore;
        if (beforeNode?.type?.name === "image") {
          const pos = $from.pos - beforeNode.nodeSize;
          wrapIntoImageRow(beforeNode, pos, $from.pos, pos);
          return;
        }

        // 6. 兜底：新建 imageRow（单张），并把光标放进行内
        editor
          .chain()
          .focus()
          .insertContent({
            type: "imageRow",
            content: [{ type: "image", attrs: { src } }],
          })
          .run();
        // 把光标移到刚建的 imageRow，下次插入能被 isActive 或 findPrevImageRowBlock 检测到
        {
          const s2 = editor.state;
          const rowBefore = findPrevImageRowBlock(s2);
          if (rowBefore && rowBefore.node.childCount > 0) {
            editor.commands.setNodeSelection(
              rowBefore.pos + rowBefore.node.nodeSize - 2,
            );
          }
        }
      } catch (error) {
        // ignore
      }
    },
    [disableImageActions, readOnly, resolveImageSrc],
  );

  const processFileList = useCallback(
    async (fileList: FileList | null | undefined) => {
      if (!fileList || fileList.length === 0 || disableImageActions || readOnly)
        return false;
      const files = Array.from(fileList).filter((file) =>
        file.type.startsWith("image/"),
      );
      if (files.length === 0) return false;
      for (const file of files) {
        await insertImage(file);
      }
      return true;
    },
    [disableImageActions, insertImage, readOnly],
  );

  const insertImageByUrl = useCallback(() => {
    if (disableImageActions || readOnly) return;
    setImageUrlDraft("");
    setUrlDialogOpen(true);
  }, [disableImageActions, readOnly]);

  const confirmInsertImageByUrl = useCallback(() => {
    if (disableImageActions || readOnly) return;
    const editor = editorRef.current;
    if (!editor) return;

    const url = imageUrlDraft.trim();
    if (!url) return;

    editor.chain().focus().setImage({ src: url }).run();
    setUrlDialogOpen(false);
    setImageUrlDraft("");
  }, [disableImageActions, imageUrlDraft, readOnly]);

  const insertTwoColumnLayout = useCallback(() => {
    const editorInstance = editorRef.current;
    if (!editorInstance || disableLayoutActions || readOnly) return;

    editorInstance
      .chain()
      .focus()
      .insertContent({
        type: "layoutBlock",
        attrs: { variant: "twoColumns" },
        content: [
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: t("editor.layoutTwoColumns", {
                  defaultValue: "Left column: add content here",
                }),
              },
            ],
          },
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: t("editor.layoutTwoColumns", {
                  defaultValue: "Right column: add content here",
                }),
              },
            ],
          },
        ],
      })
      .run();
  }, [disableLayoutActions, readOnly, t]);

  const insertFullWidthBlock = useCallback(() => {
    const editorInstance = editorRef.current;
    if (!editorInstance || disableLayoutActions || readOnly) return;

    editorInstance
      .chain()
      .focus()
      .insertContent({
        type: "layoutBlock",
        attrs: { variant: "fullWidth" },
        content: [
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: t("editor.layoutFullWidth", {
                  defaultValue:
                    "Full-width highlight block: add key message here",
                }),
              },
            ],
          },
        ],
      })
      .run();
  }, [disableLayoutActions, readOnly, t]);

  const insertCardPreset = useCallback(
    (preset: CardPreset) => {
      if (disableImageActions || disableLayoutActions || readOnly) return;
      setPendingCardPreset(preset);
      fileInputRef.current?.click();
    },
    [disableImageActions, disableLayoutActions, readOnly],
  );

  const insertCardImageLeft = useCallback(() => {
    insertCardPreset("imageLeft");
  }, [insertCardPreset]);

  const insertCardImageRight = useCallback(() => {
    insertCardPreset("imageRight");
  }, [insertCardPreset]);

  const insertCardCenter = useCallback(() => {
    insertCardPreset("center");
  }, [insertCardPreset]);

  const insertImageRow = useCallback(() => {
    if (disableImageActions || readOnly) return;
    setPendingImageRow(true);
    fileInputRef.current?.click();
  }, [disableImageActions, readOnly]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      BubbleMenu,
      ResizableImageNode.configure({ allowBase64: true }),
      ImageRow,
      LayoutBlock,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmittedHtmlRef.current = html;
      onChange(html);
    },
    onCreate: ({ editor }) => {
      editorRef.current = editor;
    },
    onDestroy: () => {
      editorRef.current = null;
    },
    editable: !readOnly,
    editorProps: {
      attributes: {
        "aria-label": "富文本编辑区",
        role: "textbox",
        tabIndex: "0",
        style: "outline: none;",
      },
      handlePaste: (_view, event) => {
        if (disableImageActions || readOnly) return false;
        const files = event.clipboardData?.files ?? null;
        if (files && files.length > 0) {
          event.preventDefault();
          void processFileList(files);
          return true;
        }
        return false;
      },
      handleDrop: (_view, event) => {
        if (disableImageActions || readOnly) return false;
        const files = event.dataTransfer?.files ?? null;
        if (files && files.length > 0) {
          event.preventDefault();
          void processFileList(files);
          return true;
        }
        return false;
      },
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    // 编辑器自己刚发出的内容回声，跳过，避免 setContent 重置选区
    if (next === lastEmittedHtmlRef.current) return;
    const current = editor.getHTML();
    if (current === next) return;
    editor.commands.setContent(next);
    lastEmittedHtmlRef.current = editor.getHTML();
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      const html = editor.getHTML();
      // 仅内容真正变化时才上报，选区变化不触发 onChange
      if (html === lastEmittedHtmlRef.current) return;
      lastEmittedHtmlRef.current = html;
      onChange(html);
    };
    editor.on("transaction", handler);
    return () => {
      editor.off("transaction", handler);
    };
  }, [editor, onChange]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawFiles = event.target.files;
      const preset = pendingCardPreset;
      const isImageRow = pendingImageRow;
      setPendingCardPreset(null);
      setPendingImageRow(false);

      if (isImageRow) {
        if (
          !rawFiles ||
          rawFiles.length === 0 ||
          disableImageActions ||
          readOnly
        ) {
          event.target.value = "";
          return;
        }

        const files = Array.from(rawFiles).filter((file) =>
          file.type.startsWith("image/"),
        );
        event.target.value = "";
        if (files.length === 0) return;

        try {
          const srcs = await Promise.all(files.map(resolveImageSrc));
          const editorInstance = editorRef.current;
          if (!editorInstance) return;

          const { state } = editorInstance;
          const { selection } = state;

          // 追加多张图片到已有 imageRow 行尾（直接操作事务）
          const appendNodesToRow = (rowPos: number, rowNode: any) => {
            const rowEndPos = rowPos + rowNode.nodeSize - 1;
            const newImgNodes = srcs.map((src) =>
              state.schema.nodes.image.create({ src }),
            );
            let tr = state.tr;
            let offset = 0;
            for (const imgNode of newImgNodes) {
              tr = tr.insert(rowEndPos + offset, imgNode);
              offset += imgNode.nodeSize;
            }
            if (offset > 0)
              tr.setSelection(
                NodeSelection.create(tr.doc, rowEndPos + offset - 1),
              );
            editorInstance.view.dispatch(tr.scrollIntoView());
            editorInstance.commands.focus();
          };

          // 1. 光标已在 imageRow 内
          if (editorInstance.isActive("imageRow")) {
            const $from = selection.$from;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === "imageRow") {
                appendNodesToRow($from.before(d), $from.node(d));
                return;
              }
            }
          }

          // 2. 前驱 block（向前扫3格）是 imageRow → 追加
          const prevImageRow2 = findPrevImageRowBlock(state);
          if (prevImageRow2) {
            appendNodesToRow(prevImageRow2.pos, prevImageRow2.node);
            return;
          }
          // 原 prevBlock 仅用于 case 3（独立图片包裹）
          const prevBlock = findPrevTopLevelBlock(state);

          // 辅助：把现有独立图片 + 新图片包裹成 imageRow
          const createImageRow = (
            existingNode: any,
            targetPos: number,
            deleteFrom: number,
            deleteTo: number,
          ) => {
            const newImageNodes = srcs.map((src) =>
              state.schema.nodes.image.create({ src }),
            );
            const existingImage = state.schema.nodes.image.create(
              existingNode.attrs,
              null,
              existingNode.marks,
            );
            const row = state.schema.nodes.imageRow.create(null, [
              existingImage,
              ...newImageNodes,
            ]);
            const tr = state.tr
              .delete(deleteFrom, deleteTo)
              .insert(targetPos, row);
            tr.setSelection(
              NodeSelection.create(tr.doc, targetPos + row.nodeSize - 2),
            );
            editorInstance.view.dispatch(tr.scrollIntoView());
            editorInstance.commands.focus();
          };

          // 3. 文档顶层前驱 block 是独立图片 → 包裹
          if (prevBlock?.node?.type?.name === "image") {
            createImageRow(
              prevBlock.node,
              prevBlock.pos,
              prevBlock.pos,
              prevBlock.pos + prevBlock.node.nodeSize,
            );
            return;
          }

          // 4. 当前选中了一张独立图片
          if (selection instanceof NodeSelection) {
            const selectedNode = state.doc.nodeAt(selection.from);
            if (selectedNode?.type.name === "image") {
              createImageRow(
                selectedNode,
                selection.from,
                selection.from,
                selection.to,
              );
              return;
            }
          }

          // 5. 光标紧邻前/后一张独立图片（直接 nodeBefore/nodeAfter）
          const $from = selection.$from;
          const beforeNode = ($from as any).nodeBefore;
          const afterNode = ($from as any).nodeAfter;
          if (beforeNode?.type?.name === "image") {
            const pos = $from.pos - beforeNode.nodeSize;
            createImageRow(beforeNode, pos, pos, pos + beforeNode.nodeSize);
            return;
          }
          if (afterNode?.type?.name === "image") {
            const imgPos = $from.pos;
            createImageRow(
              afterNode,
              imgPos,
              imgPos,
              imgPos + afterNode.nodeSize,
            );
            return;
          }

          // 6. 兜底：新建 imageRow，并把光标移到行内
          const imageContent = srcs.map((src) => ({
            type: "image",
            attrs: { src },
          }));
          editorInstance
            .chain()
            .focus()
            .insertContent({ type: "imageRow", content: imageContent })
            .run();
          {
            const s2 = editorInstance.state;
            const rowBefore = findPrevImageRowBlock(s2);
            if (rowBefore && rowBefore.node.childCount > 0) {
              editorInstance.commands.setNodeSelection(
                rowBefore.pos + rowBefore.node.nodeSize - 2,
              );
            }
          }
        } catch {
          // ignore
        }
        return;
      }

      // 没有卡片预设时，走通用图片插入逻辑
      if (!preset) {
        await processFileList(rawFiles);
        event.target.value = "";
        return;
      }

      if (
        !rawFiles ||
        rawFiles.length === 0 ||
        disableImageActions ||
        disableLayoutActions ||
        readOnly
      ) {
        event.target.value = "";
        return;
      }

      // 复制成数组，避免后续清空 input 影响 FileList
      const files = Array.from(rawFiles);
      const file = files[0];
      event.target.value = "";
      if (!file.type.startsWith("image/")) return;

      try {
        const result = await resolveImageSrc(file);
        const editorInstance = editorRef.current;
        if (!editorInstance) return;

        if (preset === "imageLeft") {
          editorInstance
            .chain()
            .focus()
            .insertContent({
              type: "layoutBlock",
              attrs: { variant: "card" },
              content: [
                {
                  type: "image",
                  attrs: {
                    src: result,
                    alignment: "left",
                    width: 360,
                  },
                },
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: t("editor.layoutImageTextCard", {
                        defaultValue: "图文卡片布局",
                      }),
                    },
                  ],
                },
              ],
            })
            .run();
        } else if (preset === "imageRight") {
          editorInstance
            .chain()
            .focus()
            .insertContent({
              type: "layoutBlock",
              attrs: { variant: "card" },
              content: [
                {
                  type: "image",
                  attrs: {
                    src: result,
                    alignment: "right",
                    width: 360,
                  },
                },
                {
                  type: "paragraph",
                  attrs: {
                    textAlign: "right",
                  },
                  content: [
                    {
                      type: "text",
                      text: t("editor.layoutImageTextCard", {
                        defaultValue: "图文卡片布局",
                      }),
                    },
                  ],
                },
              ],
            })
            .run();
        } else if (preset === "center") {
          editorInstance
            .chain()
            .focus()
            .insertContent({
              type: "layoutBlock",
              attrs: { variant: "card", cardAlign: "center" },
              content: [
                {
                  type: "image",
                  attrs: {
                    src: result,
                    alignment: "left",
                    width: 360,
                  },
                },
                {
                  type: "paragraph",
                  attrs: {
                    textAlign: "left",
                  },
                  content: [
                    {
                      type: "text",
                      text: t("editor.layoutImageTextCard", {
                        defaultValue: "图文卡片布局",
                      }),
                    },
                  ],
                },
              ],
            })
            .run();
        }
      } catch {
        // ignore
      }
    },
    [
      disableImageActions,
      disableLayoutActions,
      pendingCardPreset,
      pendingImageRow,
      processFileList,
      readOnly,
      resolveImageSrc,
      t,
    ],
  );

  return (
    <div className="space-y-2">
      <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
        <DialogContent className="bg-white/95 dark:bg-slate-950/95 backdrop-blur shadow-xl border border-emerald-50">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {insertImageUrlLabel}
            </DialogTitle>
            <DialogDescription className="text-body text-subtle">
              {imageUrlPrompt}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={imageUrlDraft}
            onChange={(e) => setImageUrlDraft(e.target.value)}
            placeholder={imageUrlPrompt}
            className="mt-2 border border-emerald-100 bg-emerald-50/40 text-body text-foreground placeholder:text-subtle focus-visible:border-emerald-300 focus-visible:ring-2 focus-visible:ring-emerald-200 dark:border-emerald-700/60 dark:bg-slate-900/60"
            autoFocus
          />
          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              className="bg-white/80 text-slate-600 hover:bg-emerald-50 dark:bg-slate-900/60 dark:text-slate-200"
              onClick={() => setUrlDialogOpen(false)}
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              className="bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm hover:from-rose-300 hover:via-orange-200 hover:to-amber-200"
              onClick={confirmInsertImageByUrl}
              disabled={!imageUrlDraft.trim()}
            >
              {insertLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div
        className={cn(
          "space-y-3",
          toolbarSticky &&
            "sticky top-0 z-10 bg-white/95 pt-1 pb-2 dark:bg-slate-950/95",
        )}
      >
        {!disableImageActions && !readOnly && (
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tracking-tight"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageUploading}
            >
              {insertImageLabel}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tracking-tight"
              onClick={insertImageByUrl}
              disabled={imageUploading}
            >
              {insertImageUrlLabel}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}
        <div className="flex w-full max-w-full flex-wrap items-center gap-2 sm:flex-nowrap">
          {editor && (
            <>
              <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                aria-label={"加粗"}
                className={`px-2 py-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("bold") ? "font-bold bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                B
              </button>
              <button
                onClick={() => editor.chain().focus().toggleItalic().run()}
                aria-label={"斜体"}
                className={`px-2 py-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("italic") ? "italic bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                I
              </button>
              <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                aria-label={"无序列表"}
                className={`px-2 py-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("bulletList") ? "bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                •
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                }
                aria-label={t("editor.heading2", { defaultValue: "Heading 2" })}
                className={`px-2 py-1 rounded text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("heading", { level: 2 }) ? "font-semibold bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                H2
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 3 }).run()
                }
                aria-label={t("editor.heading3", { defaultValue: "Heading 3" })}
                className={`px-2 py-1 rounded text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("heading", { level: 3 }) ? "font-semibold bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                H3
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                aria-label={t("editor.alignLeft", {
                  defaultValue: "Align left",
                })}
                className={`px-2 py-1 rounded text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("paragraph", { textAlign: "left" }) || editor.isActive("heading", { textAlign: "left" }) ? "bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                L
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                aria-label={t("editor.alignCenter", {
                  defaultValue: "Align center",
                })}
                className={`px-2 py-1 rounded text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("paragraph", { textAlign: "center" }) || editor.isActive("heading", { textAlign: "center" }) ? "bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                C
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                aria-label={t("editor.alignRight", {
                  defaultValue: "Align right",
                })}
                className={`px-2 py-1 rounded text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50 ${editor.isActive("paragraph", { textAlign: "right" }) || editor.isActive("heading", { textAlign: "right" }) ? "bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm" : "bg-white/80 text-slate-600 shadow-sm border border-slate-200 hover:bg-emerald-50"}`}
              >
                R
              </button>
              {!disableLayoutActions && !readOnly && (
                <>
                  <button
                    onClick={insertTwoColumnLayout}
                    aria-label={layoutTwoColumnsLabel}
                    className="px-2 py-1 rounded text-xs bg-white/80 text-slate-600 shadow-sm border border-slate-200 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50"
                  >
                    2C
                  </button>
                  <button
                    onClick={insertFullWidthBlock}
                    aria-label={layoutFullWidthLabel}
                    className="px-2 py-1 rounded text-xs bg-white/80 text-slate-600 shadow-sm border border-slate-200 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50"
                  >
                    FW
                  </button>
                  <button
                    onClick={insertCardImageLeft}
                    aria-label={layoutImageTextCardLabel}
                    className="px-2 py-1 rounded text-xs bg-white/80 text-slate-600 shadow-sm border border-slate-200 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50"
                  >
                    CL
                  </button>
                  <button
                    onClick={insertCardImageRight}
                    aria-label={layoutImageTextCardLabel}
                    className="px-2 py-1 rounded text-xs bg-white/80 text-slate-600 shadow-sm border border-slate-200 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50"
                  >
                    CR
                  </button>
                  <button
                    onClick={insertCardCenter}
                    aria-label={layoutImageTextCardLabel}
                    className="px-2 py-1 rounded text-xs bg-white/80 text-slate-600 shadow-sm border border-slate-200 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50"
                  >
                    CC
                  </button>
                  <button
                    onClick={insertImageRow}
                    aria-label={insertImageLabel}
                    title={insertImageLabel}
                    className="px-2 py-1 rounded text-xs bg-white/80 text-slate-600 shadow-sm border border-slate-200 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:ring-offset-1 focus-visible:ring-offset-emerald-50"
                  >
                    <ImagesIcon className="h-4 w-4" />
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="group/editor relative min-h-[240px] resize-y overflow-auto rounded-lg border border-emerald-100 bg-white/95 px-5 py-4 md:px-6 shadow-sm focus-within:border-emerald-300 focus-within:ring-2 focus-within:ring-emerald-200 focus-within:ring-offset-1 focus-within:ring-offset-emerald-50 dark:border-emerald-700/60 dark:bg-slate-900/80">
        <EditorContent
          editor={editor}
          className="min-h-[200px] w-full tm-lesson-editor-prose max-w-none focus:outline-none text-slate-900 dark:text-slate-100"
          style={{ outline: "none" }}
        />
      </div>
    </div>
  );
}

// Demo用法
export function RichTextEditorDemo() {
  const [value, setValue] = React.useState("<p>欢迎使用富文本编辑器！</p>");
  return (
    <div>
      <h3>富文本编辑器Demo</h3>
      <RichTextEditor value={value} onChange={setValue} />
      <div className="mt-4 text-sm text-muted-foreground">
        支持加粗、斜体、无序列表，键盘可达，屏幕阅读器友好。
      </div>
    </div>
  );
}
